import {add, differenceInDays} from 'date-fns';
import {DateFormatEnum} from 'presentation/constants';
import {useAccountDataStore} from 'presentation/pages/private/store/useAccountDataStateStore';
import {translations} from 'presentation/translations-new';
import {parseDate, stringfyDate} from 'presentation/utils';
import {useMemo} from 'react';

const {day, days, years, year} = translations['pt-br'].privateFlow.canceledContractFindOutMore;

export enum ContractStatusEnum {
	CANCELLED_LESS_THAN_THREE_MONTHS = 'CANCELLED_LESS_THAN_THREE_MONTHS',
	CANCELLED_LESS_THAN_ONE_YEAR = 'CANCELLED_LESS_THAN_ONE_YEAR',
	CANCELLED_ONE_YEAR_AND_THREE_MONTHS = 'CANCELLED_ONE_YEAR_AND_THREE_MONTHS',
	CANCELLED_LESS_THAN_TWO_YEARS = 'CANCELLED_LESS_THAN_TWO_YEARS',
	CANCELLED_LESS_THAN_FIVE_YEARS = 'CANCELLED_LESS_THAN_FIVE_YEARS',
	CANCELLED_MORE_THAN_FIVE_YEARS = 'CANCELLED_MORE_THAN_FIVE_YEARS',
	VALID = 'VALID',
}

const today = new Date();

const TIME_IN_DAYS = {
	THREE_MONTHS: differenceInDays(add(today, {days: 90}), today),
	ONE_YEAR: differenceInDays(add(today, {years: 1}), today),
	ONE_YEAR_AND_THREE_MONTHS: differenceInDays(add(today, {years: 1, months: 3}), today),
	TWO_YEARS: differenceInDays(add(today, {years: 2}), today),
	FIVE_YEARS: differenceInDays(add(today, {years: 5}), today),
};

const getCancelledPeriod = (cancelledAt: Date): ContractStatusEnum => {
	const interval = differenceInDays(today, cancelledAt);

	if (interval >= TIME_IN_DAYS.FIVE_YEARS)
		return ContractStatusEnum.CANCELLED_MORE_THAN_FIVE_YEARS;
	if (interval > TIME_IN_DAYS.TWO_YEARS) return ContractStatusEnum.CANCELLED_LESS_THAN_FIVE_YEARS;
	if (interval > TIME_IN_DAYS.ONE_YEAR_AND_THREE_MONTHS)
		return ContractStatusEnum.CANCELLED_LESS_THAN_TWO_YEARS;
	if (interval > TIME_IN_DAYS.ONE_YEAR)
		return ContractStatusEnum.CANCELLED_ONE_YEAR_AND_THREE_MONTHS;
	if (interval > TIME_IN_DAYS.THREE_MONTHS)
		return ContractStatusEnum.CANCELLED_LESS_THAN_ONE_YEAR;
	if (interval <= TIME_IN_DAYS.THREE_MONTHS)
		return ContractStatusEnum.CANCELLED_LESS_THAN_THREE_MONTHS;

	return ContractStatusEnum.VALID;
};

const formatCancellationPeriod = (cancelledAt: Date): string => {
	const totalDays = differenceInDays(today, cancelledAt);
	const totalYears = Math.floor(totalDays / 365);
	const remainingDays = totalDays % 365;

	const formattedPeriodDate: string[] = [];

	if (totalDays === 0) {
		return `0 ${days}`;
	}

	if (totalYears > 0) {
		formattedPeriodDate.push(`${totalYears} ${totalYears === 1 ? year : years}`);
	}
	if (remainingDays > 0) {
		formattedPeriodDate.push(`${remainingDays} ${remainingDays === 1 ? day : days}`);
	}
	if (totalDays < 0) {
		formattedPeriodDate.push(`1 ${day}`);
	}

	return formattedPeriodDate.join(' e ');
};

export function useContractStatus() {
	const {beneficiary, planBeneficiaries} = useAccountDataStore(state => state.accountData);

	const isContractSuspended = beneficiary?.acessoSuspenso;

	const isPlanCancelledDatesAreEqual = Boolean(
		planBeneficiaries?.every(
			(currentBeneficiary, _index, prevListBeneficiaries) =>
				currentBeneficiary?.dtCancelamentoC === prevListBeneficiaries[0]?.dtCancelamentoC,
		),
	);

	const isPlanAllCancelled = Boolean(
		planBeneficiaries?.every(planBeneficiary => planBeneficiary.dtCancelamentoC != null),
	);

	const isAnyPlanCancelled = planBeneficiaries?.some(
		planBeneficiary => planBeneficiary?.dtCancelamentoC !== null,
	);

	const contractCancellationDate = useMemo(() => {
		if (beneficiary.dataCancelamento) {
			return parseDate(beneficiary.dataCancelamento, DateFormatEnum.DATE_FORMAT_2);
		}
		return undefined;
	}, [beneficiary]);

	const contractCancellationDateString = useMemo(() => {
		if (contractCancellationDate) {
			return stringfyDate(contractCancellationDate, DateFormatEnum.DATE_FORMAT_8);
		}
		return undefined;
	}, [contractCancellationDate]);

	const initialStatus = useMemo(() => {
		if (!contractCancellationDate) return {status: ContractStatusEnum.VALID, period: ''};
		return {
			status: getCancelledPeriod(contractCancellationDate),
			period: formatCancellationPeriod(contractCancellationDate),
		};
	}, [contractCancellationDate]);

	const {status, period} = initialStatus;

	const isValid = status === ContractStatusEnum.VALID;
	const isCancelledLessThanThreeMonths =
		status === ContractStatusEnum.CANCELLED_LESS_THAN_THREE_MONTHS;
	const isCancelledAtOneYear =
		isCancelledLessThanThreeMonths ||
		status === ContractStatusEnum.CANCELLED_LESS_THAN_ONE_YEAR;
	const isCancelledAtOneYearAndThreeMonths =
		isCancelledAtOneYear || status === ContractStatusEnum.CANCELLED_ONE_YEAR_AND_THREE_MONTHS;
	const isCancelledAtTwoYears =
		isCancelledAtOneYearAndThreeMonths ||
		status === ContractStatusEnum.CANCELLED_LESS_THAN_TWO_YEARS;
	const isCancelledAtFiveYears =
		isCancelledAtTwoYears || status === ContractStatusEnum.CANCELLED_LESS_THAN_FIVE_YEARS;
	const isCancelledMoreThanFiveYears =
		status === ContractStatusEnum.CANCELLED_MORE_THAN_FIVE_YEARS;

	return {
		status,
		formattedPeriod: period,
		isContractSuspended,
		contractCancellationDate,
		contractCancellationDateString,
		isValid,
		isCancelledLessThanThreeMonths,
		isCancelledAtOneYear,
		isCancelledAtOneYearAndThreeMonths,
		isCancelledAtTwoYears,
		isCancelledAtFiveYears,
		isCancelledMoreThanFiveYears,
		isPlanAllCancelled,
		isPlanCancelledDatesAreEqual,
		isAnyPlanCancelled,
	};
}
